// ================================================================
// CompaniesOverview — صفحه‌ی فرودِ مدیرکل: نمای کلیِ همه‌ی شرکت‌ها.
// کلیک روی یک شرکت → سناریوی همان شرکت روی canvas باز می‌شود.
// ================================================================
import './companies-overview.css';
import { t } from '../../i18n/index.js';
import { icon, escapeHtml, delegate } from '../../utils/dom.js';
import { getUser } from '../../core/auth.js';
import { listOrgs } from '../../core/platform.js';
import { openPlatformPanel } from '../platform/PlatformPanel.js';
import { openCompanyScenario } from '../../core/scenarioStore.js';

let containerEl = null;
const s = { loading: false, error: '', companies: [], query: '' };

// fallback i18n: اگر کلید نبود، متنِ پیش‌فرض
function tx(key, fallback) { const v = t(key); return v && v !== key ? v : fallback; }

const GRADS = [
  ['#10b981', '#059669'], ['#3b82f6', '#6366f1'], ['#8b5cf6', '#6366f1'],
  ['#f59e0b', '#ec4899'], ['#06b6d4', '#3b82f6'], ['#f43f5e', '#f97316'],
];
function grad(key) {
  let h = 0; const str = String(key || '');
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  const g = GRADS[h % GRADS.length];
  return `linear-gradient(135deg, ${g[0]}, ${g[1]})`;
}
function initials(name) {
  const base = String(name || '?').trim();
  const parts = base.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

export function renderCompaniesOverview(container) {
  containerEl = container;

  delegate(containerEl, '[data-action]', 'click', (e, el) => {
    const a = el.dataset.action;
    if (a === 'open-company') openCompany(el.dataset.id);
    else if (a === 'manage') openPlatformPanel();
    else if (a === 'reload') load();
  });

  containerEl.addEventListener('input', (e) => {
    if (e.target.matches('.cov-search-input')) { s.query = e.target.value; drawGrid(); }
  });

  load();
}

export function refreshOverview() {
  if (containerEl) load();
}

async function load() {
  s.loading = true; s.error = ''; draw();
  try {
    s.companies = (await listOrgs()).organizations || [];
  } catch (e) {
    s.error = e.message || tx('overview.error', 'Could not load companies.');
  } finally {
    s.loading = false; draw();
  }
}

function openCompany(id) {
  openCompanyScenario(id).catch((err) => console.warn('Could not open company scenario:', err));
}

function filtered() {
  const q = s.query.trim().toLowerCase();
  if (!q) return s.companies;
  return s.companies.filter((c) =>
    (c.name || '').toLowerCase().includes(q) || (c.slug || '').toLowerCase().includes(q),
  );
}

function draw() {
  if (!containerEl) return;
  const u = getUser() || {};
  const name = (u.name || u.email || '').split(/\s+/)[0] || '';

  containerEl.innerHTML = `
    <div class="cov">
      <header class="cov-head">
        <div>
          <h1 class="cov-title">${escapeHtml(tx('overview.welcome', 'Welcome'))}${name ? ', ' + escapeHtml(name) : ''}</h1>
          <p class="cov-sub">${escapeHtml(tx('overview.subtitle', 'Pick a company to open its scenario.'))}</p>
        </div>
        <button class="cov-btn" data-action="manage">${icon('settings', 18)}<span>${escapeHtml(tx('overview.manage', 'Manage companies'))}</span></button>
      </header>

      <div class="cov-search">
        ${icon('search', 18)}
        <input class="cov-search-input" type="text" autocomplete="off"
          placeholder="${escapeHtml(tx('overview.search', 'Search company…'))}"
          value="${escapeHtml(s.query)}" />
      </div>

      <div class="cov-grid" id="cov-grid">${gridHtml()}</div>
    </div>
  `;
}

function drawGrid() {
  const g = containerEl.querySelector('#cov-grid');
  if (g) g.innerHTML = gridHtml();
}

function gridHtml() {
  if (s.loading) return `<p class="cov-muted">${escapeHtml(tx('overview.loading', 'Loading…'))}</p>`;
  if (s.error) {
    return `<p class="cov-muted cov-error">${escapeHtml(s.error)}</p>
      <button class="cov-btn" data-action="reload">${escapeHtml(tx('overview.retry', 'Retry'))}</button>`;
  }
  const items = filtered();
  if (!items.length) return `<p class="cov-muted">${escapeHtml(tx('overview.empty', 'No companies found.'))}</p>`;
  return items.map(card).join('');
}

function card(c) {
  const counts = c._count || {};
  const suspended = c.status === 'SUSPENDED';
  const platformBadge = c.isPlatform
    ? `<span class="cov-badge cov-badge-platform">${escapeHtml(tx('overview.platform', 'Platform'))}</span>` : '';
  const statusBadge = suspended
    ? `<span class="cov-badge cov-badge-suspended">${escapeHtml(tx('overview.suspended', 'Suspended'))}</span>` : '';

  return `
    <button class="cov-card" data-action="open-company" data-id="${escapeHtml(c.id)}">
      <div class="cov-card-top">
        <div class="cov-avatar" style="background:${grad(c.name)}">${escapeHtml(initials(c.name))}</div>
        <div class="cov-card-titles">
          <div class="cov-card-name">${escapeHtml(c.name)} ${platformBadge}${statusBadge}</div>
          <div class="cov-card-slug">${escapeHtml(c.slug || '')}</div>
        </div>
      </div>
      <div class="cov-card-stats">
        <span title="${escapeHtml(tx('overview.users', 'Users'))}">${icon('group', 16)} ${counts.users ?? 0}</span>
        <span title="${escapeHtml(tx('overview.connections', 'Connections'))}">${icon('cable', 16)} ${counts.connections ?? 0}</span>
        <span title="${escapeHtml(tx('overview.scenarios', 'Scenarios'))}">${icon('account_tree', 16)} ${counts.scenarios ?? 0}</span>
      </div>
      <div class="cov-card-go">${escapeHtml(tx('overview.open', 'Open scenario'))} ${icon('arrow_forward', 16)}</div>
    </button>
  `;
}
